import { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trocarSenhaPropria } from '../services/api';
import { APP_VERSION } from '../version';
import logo from '../logo-genpac.png';
import './Layout.css';

const nav = [
  { to: '/dashboard',  label: 'Dashboard',  icon: <IcoDash /> },
  { to: '/produtos',   label: 'Produtos',   icon: <IcoProd /> },
  { to: '/clientes',   label: 'Clientes',   icon: <IcoCli /> },
  { to: '/vendas',     label: 'Vendas',     icon: <IcoVend /> },
  { to: '/financeiro', label: 'Financeiro', icon: <IcoFin /> },
  { to: '/compras',    label: 'Compras',    icon: <IcoComp /> },
];

const navAdmin = [
  { to: '/admin/empresas', label: 'Empresas',       icon: <IcoEmp /> },
  { to: '/admin/usuarios', label: 'Usuários',        icon: <IcoUsu /> },
  { to: '/admin/logs',     label: 'Logs de Acesso',  icon: <IcoLog /> },
];

export default function Layout({ children }) {
  const { sair, nome, empresa, isAdmin, empresaId, empresas, trocarEmpresa } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [mini,        setMini]        = useState(false);
  const [trocando,    setTrocando]    = useState(false);
  const [showSwitch,  setShowSwitch]  = useState(false);
  const [modalSenha,  setModalSenha]  = useState(false);
  const [senhaForm,   setSenhaForm]   = useState({ atual: '', nova: '', confirmar: '' });
  const [senhaErro,   setSenhaErro]   = useState('');
  const [senhaOk,     setSenhaOk]     = useState('');
  const [salvandoSenha, setSalvandoSenha] = useState(false);

  const allNav = isAdmin ? [...nav, ...navAdmin] : nav;
  const temMultiplas = empresas.length > 1;

  const handleTrocar = async (id) => {
    if (id === empresaId || trocando) return;
    setTrocando(true);
    setShowSwitch(false);
    try {
      await trocarEmpresa(id);
      // Recarrega a página atual para limpar dados em memória das outras páginas
      navigate(location.pathname, { replace: true });
      window.location.reload();
    } catch (err) {
      alert('Erro ao trocar de empresa: ' + err.message);
    } finally {
      setTrocando(false);
    }
  };

  const abrirModalSenha = () => {
    setSenhaForm({ atual: '', nova: '', confirmar: '' });
    setSenhaErro(''); setSenhaOk('');
    setModalSenha(true);
  };

  const handleTrocarSenha = async (e) => {
    e.preventDefault();
    setSenhaErro(''); setSenhaOk('');
    if (senhaForm.nova !== senhaForm.confirmar) {
      setSenhaErro('A nova senha e a confirmação não conferem.'); return;
    }
    setSalvandoSenha(true);
    try {
      await trocarSenhaPropria(senhaForm.atual, senhaForm.nova);
      setSenhaOk('Senha alterada com sucesso!');
      setSenhaForm({ atual: '', nova: '', confirmar: '' });
    } catch (err) {
      setSenhaErro(err.message || 'Erro ao alterar senha.');
    } finally {
      setSalvandoSenha(false);
    }
  };

  return (
    <div className={`layout ${mini ? 'layout--mini' : ''}`}>
      {/* Sidebar — desktop only */}
      <aside className="sidebar">
        <div className="sb-logo">
          <div className="sb-logo-icon">
            <img src={logo} alt="Genpac" className="sb-logo-img" />
          </div>
          {!mini && (
            <div className="sb-logo-text">
              <span className="sb-logo-nome">arauto</span>
              <span className="sb-logo-sub">360</span>
            </div>
          )}
          <button className="sb-toggle" onClick={() => setMini(v => !v)}>
            {mini ? '›' : '‹'}
          </button>
        </div>

        {!mini && (
          <div className="sb-empresa">
            <span className="sb-empresa-label">Empresa</span>
            {temMultiplas ? (
              <div className="sb-empresa-switch-wrap">
                <button
                  className="sb-empresa-switch"
                  onClick={() => setShowSwitch(v => !v)}
                  disabled={trocando}
                  title="Trocar de empresa"
                >
                  <span className="sb-empresa-nome">{trocando ? 'Trocando...' : empresa}</span>
                  <span className="sb-empresa-chevron">{showSwitch ? '▲' : '▼'}</span>
                </button>
                {showSwitch && (
                  <div className="sb-empresa-dropdown">
                    {empresas.map(e => (
                      <button
                        key={e.id}
                        className={`sb-empresa-option${e.id === empresaId ? ' sb-empresa-option--ativa' : ''}`}
                        onClick={() => handleTrocar(e.id)}
                        disabled={trocando}
                      >
                        {e.id === empresaId && <span className="sb-empresa-check">✓</span>}
                        {e.nome}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <span className="sb-empresa-nome">{empresa}</span>
            )}
          </div>
        )}

        <nav className="sb-nav">
          {nav.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `sb-link${isActive ? ' sb-link--ativo' : ''}`}
              title={mini ? item.label : ''}
            >
              <span className="sb-link-icon">{item.icon}</span>
              {!mini && <span className="sb-link-label">{item.label}</span>}
            </NavLink>
          ))}

          {isAdmin && (
            <>
              {!mini && <div className="sb-section-label">Administração</div>}
              {navAdmin.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `sb-link sb-link--admin${isActive ? ' sb-link--ativo' : ''}`}
                  title={mini ? item.label : ''}
                >
                  <span className="sb-link-icon">{item.icon}</span>
                  {!mini && <span className="sb-link-label">{item.label}</span>}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {!mini && <div className="sb-version">v{APP_VERSION}</div>}
        <div className="sb-bottom">
          {!mini && (
            <div className="sb-user">
              <div className="sb-user-avatar">{nome?.[0]?.toUpperCase() || 'U'}</div>
              <div className="sb-user-info">
                <span className="sb-user-nome">{nome}</span>
                {isAdmin && <span className="sb-user-role">Admin</span>}
              </div>
            </div>
          )}
          <button className="sb-sair" onClick={abrirModalSenha} title="Alterar senha">
            <IcoChave />
          </button>
          <button
            className="sb-sair"
            onClick={() => { sair(); navigate('/'); }}
            title="Sair"
          >
            <IcoSair />
          </button>
        </div>
      </aside>

      {/* Top header — mobile only */}
      <header className="mobile-header">
        <div className="mh-logo">
          <div className="mh-logo-icon">
            <img src={logo} alt="Genpac" className="mh-logo-img" />
          </div>
          <span className="mh-logo-text">arauto<span className="mh-logo-360">360</span></span>
        </div>
        <div className="mh-center">
          {temMultiplas ? (
            <select
              className="mh-empresa-select"
              value={empresaId || ''}
              onChange={e => handleTrocar(parseInt(e.target.value))}
              disabled={trocando}
            >
              {empresas.map(e => (
                <option key={e.id} value={e.id}>{e.nome}</option>
              ))}
            </select>
          ) : (
            <span className="mh-empresa">{empresa}</span>
          )}
        </div>
        <div className="mh-right">
          <div className="mh-avatar" title={nome}>{nome?.[0]?.toUpperCase() || 'U'}</div>
          <button className="mh-sair" onClick={() => { sair(); navigate('/'); }} title="Sair">
            <IcoSair />
          </button>
        </div>
      </header>

      {/* Modal troca de senha */}
      {modalSenha && (
        <div className="ls-modal-bg" onClick={() => setModalSenha(false)}>
          <div className="ls-modal" onClick={e => e.stopPropagation()}>
            <div className="ls-modal-header">
              <h2>Alterar senha</h2>
              <button className="ls-modal-close" onClick={() => setModalSenha(false)}>✕</button>
            </div>
            <form className="ls-modal-form" onSubmit={handleTrocarSenha}>
              <div className="ls-field">
                <label>Senha atual</label>
                <input
                  type="password" required autoFocus
                  value={senhaForm.atual}
                  onChange={e => setSenhaForm(f => ({ ...f, atual: e.target.value }))}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>
              <div className="ls-field">
                <label>Nova senha</label>
                <input
                  type="password" required minLength={6}
                  value={senhaForm.nova}
                  onChange={e => setSenhaForm(f => ({ ...f, nova: e.target.value }))}
                  placeholder="mínimo 6 caracteres"
                  autoComplete="new-password"
                />
              </div>
              <div className="ls-field">
                <label>Confirmar nova senha</label>
                <input
                  type="password" required minLength={6}
                  value={senhaForm.confirmar}
                  onChange={e => setSenhaForm(f => ({ ...f, confirmar: e.target.value }))}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
              {senhaErro && <div className="ls-erro">{senhaErro}</div>}
              {senhaOk   && <div className="ls-ok">{senhaOk}</div>}
              <button className="ls-btn" type="submit" disabled={salvandoSenha}>
                {salvandoSenha ? 'Salvando...' : 'Alterar senha'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="layout-main">
        <div className="layout-content fade-in">{children}</div>
      </main>

      {/* Bottom navigation — mobile only */}
      <nav className="mobile-nav">
        {allNav.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `mn-item${isActive ? ' mn-item--ativo' : ''}`}
          >
            <span className="mn-icon">{item.icon}</span>
            <span className="mn-label">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

function IcoDash() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>; }
function IcoProd() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8l-9-5-9 5v8l9 5 9-5z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>; }
function IcoCli()  { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>; }
function IcoVend() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>; }
function IcoFin()  { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/><path d="M9 10.5h6M9 7.5h3"/></svg>; }
function IcoSair() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>; }
function IcoEmp()  { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>; }
function IcoUsu()  { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>; }
function IcoLog()   { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>; }
function IcoChave() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="8" cy="15" r="5"/><path d="M21 3l-9.4 9.4M15 9l3 3"/></svg>; }
function IcoComp()  { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>; }
